/**
 * Bridges an AI graph mutation to the review surfaces: it captures a single
 * pre-batch undo snapshot (so Cmd+Z treats the whole batch as one step),
 * commits the mutation, then emits a diff card and pulses the touched nodes.
 *
 * Kept out of tool-executor's per-op logic so the batch→card→undo contract is
 * unit-testable in isolation.
 */
import { useHistoryStore } from '@/features/backtest/stores/history-store'
import { useEditDiffStore, nextBatchId } from './edit-diff-store'
import { pulseNodes } from './node-pulse'
import type { DiffLine } from './edit-diff'

export interface RecordGraphEditOptions {
  /** Card header, e.g. "Strategy edited". */
  title: string
  /** Human-readable diff lines (from buildEditDiff, or a build summary). */
  diffLines: DiffLine[]
  /** Node ids to pulse-highlight on land (post-apply ids). */
  affectedIds?: string[]
  /** Applies the mutation to the live graph. Runs after the snapshot is taken. */
  commit: () => void
}

/**
 * Take one undo snapshot, commit, then record a diff card and pulse. Returns
 * the batch id. The snapshot captured here is the exact HistoryItem the card's
 * Undo restores, matching Cmd+Z.
 */
export function recordGraphEdit(opts: RecordGraphEditOptions): string {
  const history = useHistoryStore.getState()
  // One snapshot BEFORE the commit → one Cmd+Z step for the whole batch.
  history.takeSnapshot()
  const snapshot = useHistoryStore.getState().past.at(-1)

  opts.commit()

  const batchId = nextBatchId()
  useEditDiffStore.getState().addCard({
    batchId,
    title: opts.title,
    diffLines: opts.diffLines,
    snapshot,
  })

  if (opts.affectedIds && opts.affectedIds.length > 0) pulseNodes(opts.affectedIds)
  return batchId
}

/** The public editor methods settle asynchronously; record only after they apply. */
export async function recordAsyncGraphEdit<T>(title: string, commit: () => Promise<T>): Promise<T> {
  useHistoryStore.getState().takeSnapshot()
  const snapshot = useHistoryStore.getState().past.at(-1)
  const result = await commit()
  useEditDiffStore.getState().addCard({
    batchId: nextBatchId(),
    title,
    snapshot,
    diffLines: [{ kind: 'set_params', text: title }],
  })
  return result
}
